import { useCartDataProvider } from "context/CartProductContext";
import { Product } from "data/data";
import { useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import CartItem from "components/Cart/CartItem";
import {
  calculatingTotalPrice,
  calculatingTotalTax,
  aggregateTaxDetails,
  buildTaxDisplayList,
  TaxTotal,
} from "utils/priceCalculations";
import { ShoppingBagIcon } from "@heroicons/react/24/outline";

const CartPage = () => {
  const { Products, dispatch, searchProduct }: any = useCartDataProvider();
  const navigate = useNavigate();
  const merchantname = Cookies.get("merchantname");
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleCheckout = () => {
    const userDataString = Cookies.get("userDataaInfo");
    const SignupData = Cookies.get("SignupData");
    // Logged-in users go straight to checkout; guests collect details first
    if (userDataString || SignupData) {
      navigate(`/${merchantname}/checkout`);
    } else {
      navigate(`/${merchantname}/userdetails`);
    }
  };

  const handleEditItem = (item: Product) => {
    navigate(`/${merchantname}/product-detail/${item.id}`, {
      state: {
        modifierChecked: item.modifierChecked || {},
        nestedModifierSelections: (item as any).nestedModifierSelections || {},
        cartItemId: item.cartItemId,
        quantity: item.quantity,
        note: item.note,
      },
    });
  };

  const handleClearCart = () => {
    dispatch({ type: "CLEAR_CART" });
    setShowClearConfirm(false);
  };

  // Calculate totals
  const filteredProducts = Products.filter((item: any) =>
    item.name.toLowerCase().includes(searchProduct.toLowerCase())
  );

  const allProductPrice = filteredProducts
    .reduce(
      (acc: number, item: Product) => acc + calculatingTotalPrice(item),
      0
    )
    .toFixed(2);

  const taxDetails = aggregateTaxDetails(filteredProducts);
  const taxDisplayList = buildTaxDisplayList(taxDetails);

  const allTaxTotals: TaxTotal[] = filteredProducts.flatMap(
    (product: Product) => calculatingTotalTax(product)
  );
  const taxTotalsByName = allTaxTotals.reduce(
    (acc: Record<string, number>, t: TaxTotal) => {
      acc[t.name] = (acc[t.name] || 0) + t.amount;
      return acc;
    },
    {}
  );
  const taxDisplayTotals = Object.values(taxTotalsByName).map((amt: number) =>
    amt.toFixed(2)
  );

  const amtTax = Object.values(taxTotalsByName).reduce(
    (acc: number, amt: number) => acc + amt,
    0
  );
  const totAmt = parseFloat(allProductPrice) + amtTax;

  if (Products.length === 0) {
    return (
      <div className="nc-CartPage bg-white dark:bg-slate-900 min-h-[60vh]">
        <main className="container py-16 lg:pb-28 lg:pt-20">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-24 h-24 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-6">
              <ShoppingBagIcon className="w-12 h-12 text-slate-300 dark:text-slate-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              Your cart is empty
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-8">
              Browse our menu to start adding items
            </p>
            <ButtonPrimary href={`/${merchantname}`}>Browse Menu</ButtonPrimary>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="nc-CartPage bg-white dark:bg-slate-900 min-h-[60vh]">
      <main className="container py-6 lg:pb-28 lg:pt-12">
        {/* Breadcrumb */}
        <div className="mb-6">
          <h2 className="block text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">
            Your Order
          </h2>
          <div className="flex items-center gap-1.5 mt-2 text-sm text-slate-500 dark:text-slate-400">
            <Link
              to={`/${merchantname}`}
              className="hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              Menu
            </Link>
            <span>/</span>
            <span className="text-slate-900 dark:text-slate-100 font-medium">
              Cart
            </span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Cart items */}
          <div className="flex-1">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-4">
              {filteredProducts.map((item: Product, index: number) => (
                <CartItem
                  key={item.cartItemId || index}
                  item={item}
                  onEdit={handleEditItem}
                />
              ))}
            </div>

            {/* Clear cart */}
            <div className="mt-4">
              {!showClearConfirm ? (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="text-sm text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                >
                  Clear Cart
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Clear all items?
                  </span>
                  <button
                    onClick={handleClearCart}
                    className="text-sm font-semibold text-red-500 dark:text-red-400"
                  >
                    Yes, clear
                  </button>
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="text-sm font-semibold text-slate-500 dark:text-slate-400"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Order summary */}
          <div className="lg:w-[380px]">
            <div className="sticky top-20 bg-slate-50 dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-5">
                Order Summary
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Subtotal</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    ${allProductPrice}
                  </span>
                </div>

                {taxDisplayList.map((taxDisplay, index) => (
                  <div
                    key={index}
                    className="flex justify-between text-slate-500 dark:text-slate-400"
                  >
                    <span>{taxDisplay}</span>
                    {taxDisplayTotals[index] && (
                      <span className="font-semibold text-slate-900 dark:text-slate-100">
                        ${taxDisplayTotals[index]}
                      </span>
                    )}
                  </div>
                ))}

                <div className="border-t border-slate-200 dark:border-slate-700 pt-3 flex justify-between">
                  <span className="font-bold text-slate-900 dark:text-slate-100">
                    Order Total
                  </span>
                  <span className="font-bold text-lg text-slate-900 dark:text-slate-100">
                    ${totAmt.toFixed(2)}
                  </span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full mt-6 py-3.5 bg-accent-500 dark:bg-accent-400 text-white dark:text-slate-900 rounded-xl font-bold text-base hover:bg-accent-600 dark:hover:bg-accent-500 transition-colors shadow-md"
              >
                Checkout - ${totAmt.toFixed(2)}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CartPage;
